import { Editor } from 'components/Editor/Editor'
import { EditorRHF } from 'components/Editor/EditorRHF'
import { FloatingTooltip } from 'components/Tooltip/FloatingTooltip'
import { INote } from 'models/contact_companies'
import { useState, useMemo, Fragment } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { PiPaperclipHorizontalBold, PiX } from 'react-icons/pi'
import { toast } from 'react-toastify'
import { API_HOST } from 'utils/http-client'
import { useFileAttachments } from 'hooks/useFileAttachments'
import {
  ALLOWED_NOTE_ATTACHMENT_TYPES,
  MAX_NOTE_ATTACHMENT_SIZE,
  getAcceptString,
} from 'constants/fileTypes'
import {
  AttachmentList,
  Attachment,
  isNewAttachment,
} from 'components/Attachments/AttachmentList'

const MAX_FILENAME_LENGTH = 20

/** Truncate a filename before its extension, preserving the extension. */
function truncateFilename(name: string, maxLen = MAX_FILENAME_LENGTH): string {
  if (name.length <= maxLen) return name
  const dotIndex = name.lastIndexOf('.')
  if (dotIndex <= 0) return name.slice(0, maxLen - 3) + '...'
  const ext = name.slice(dotIndex)
  const base = name.slice(0, dotIndex)
  const available = maxLen - ext.length - 3 // 3 for "..."
  if (available <= 0) return name.slice(0, maxLen - 3) + '...'
  return base.slice(0, available) + '...' + ext
}

interface NoteManagerProps<T extends FieldValues> {
  control: Control<T>
  noteName: Path<T>
  attachmentsName: Path<T>
  label?: string
  placeholder?: string
  existingNote?: INote
  viewMode?: boolean
  editorClassName?: string
  enableMentions?: boolean
  disabled?: boolean
  onAttachmentsToDelete?: (attachmentIds: number[]) => void
  /** Content rendered after the toolbar, pushed to the right (e.g. a Save button). */
  toolbarSuffix?: React.ReactNode
  /** Additional className applied to the toolbar wrapper (e.g. background color). */
  toolbarClassName?: string
}

export const NoteManager = <T extends FieldValues>(
  props: NoteManagerProps<T>
) => {
  const {
    control,
    noteName,
    attachmentsName,
    label,
    placeholder,
    existingNote,
    viewMode,
    editorClassName = 'h-28',
    enableMentions = false,
    disabled = false,
  } = {
    placeholder: 'Start writing your note here',
    label: 'New Note',
    ...props,
  }

  const [existingAttachments, setExistingAttachments] = useState(
    existingNote?.attachments
  )

  const [_attachmentsToDelete, setAttachmentsToDelete] = useState<number[]>([])

  const {
    field: { onChange: attachmentsOnChange, value: attachmentsValue },
  } = useController({ name: attachmentsName, control }) as {
    field: { onChange: (value: File[]) => void; value: File[] }
  }

  const {
    fileInputRef: inputRef,
    openFileSelect,
    handleFileSelect,
  } = useFileAttachments({
    allowedTypes: ALLOWED_NOTE_ATTACHMENT_TYPES,
    maxFileSize: MAX_NOTE_ATTACHMENT_SIZE,
    maxTotalSize: MAX_NOTE_ATTACHMENT_SIZE,
    onAdd: (newAttachments) => {
      const files = newAttachments
        .filter(isNewAttachment)
        .map((att) => att.file)
      attachmentsOnChange([...attachmentsValue, ...files])
    },
  })

  const handleRemoveExistingAttachment = (index: number) => {
    if (!existingAttachments) return
    const attachment = existingAttachments[index]

    // Track for deletion (API call happens on save)
    if (attachment.id) {
      setAttachmentsToDelete((prev) => {
        const updated = [...prev, attachment.id]
        props.onAttachmentsToDelete?.(updated)
        return updated
      })
    }

    // Remove from display immediately
    setExistingAttachments((existingAttachments) =>
      existingAttachments?.filter((_, i) => i !== index)
    )
    toast.info('Attachment will be removed when you save')
  }

  // Combine existing and new attachments for AttachmentList
  const allAttachments = useMemo(() => {
    const combined: Attachment[] = []

    // Add existing attachments from server
    if (existingAttachments) {
      combined.push(
        ...existingAttachments.map((att) => ({
          id: att.id,
          filename: att.original_filename,
          size: att.size || 0, // Use size from backend
          file_url: att.file?.startsWith('/')
            ? `${API_HOST}${att.file}`
            : att.file,
        }))
      )
    }

    // Add new attachments (Files)
    if (!viewMode && attachmentsValue) {
      combined.push(
        ...attachmentsValue.map((file) => ({
          file,
          filename: file.name,
          size: file.size,
        }))
      )
    }

    return combined
  }, [existingAttachments, attachmentsValue, viewMode])

  const shouldShowSummary = allAttachments.length > 0

  const handleRemoveAttachment = (index: number) => {
    const existingCount = existingAttachments?.length || 0

    if (index < existingCount) {
      // Remove existing attachment
      handleRemoveExistingAttachment(index)
    } else {
      // Remove new attachment
      const newIndex = index - existingCount
      const newAttachments = attachmentsValue.filter((_, i) => i !== newIndex)
      attachmentsOnChange(newAttachments)
      toast.info('Attachment removed')
    }
  }

  /** Compose toolbar suffix: inline attachment chips + parent's toolbarSuffix. */
  const composedToolbarSuffix = useMemo(() => {
    const chips =
      !viewMode && allAttachments.length > 0 ? (
        <>
          {allAttachments.map((att, i) => {
            const display = truncateFilename(att.filename)
            const isTruncated = display !== att.filename
            // DS2026 neutral badge: gray-100 fill, gray-700 text, gray-500
            // icon, fully rounded — mirrors the StatusBadge `neutral` variant.
            const chip = (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ds2026-badge-gray-bg px-2.5 py-1 text-sm font-medium text-ds2026-badge-gray-text max-w-[180px]">
                <PiPaperclipHorizontalBold
                  size={12}
                  className="flex-shrink-0 text-ds2026-badge-gray-icon"
                />
                <span className="truncate">{display}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(i)}
                    className="flex-shrink-0 hover:text-error transition-colors"
                  >
                    <PiX size={12} />
                  </button>
                )}
              </span>
            )
            // Only attach the dark tooltip when the name was actually
            // shortened (shows "…") — a full name needs no hover affordance.
            return isTruncated ? (
              <FloatingTooltip key={i} theme="dark" text={att.filename}>
                {chip}
              </FloatingTooltip>
            ) : (
              <Fragment key={i}>{chip}</Fragment>
            )
          })}
        </>
      ) : null

    if (!chips && !props.toolbarSuffix) return undefined
    return (
      <>
        {chips}
        {props.toolbarSuffix}
      </>
    )
  }, [allAttachments, viewMode, disabled, props.toolbarSuffix]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {viewMode ? (
        <Editor
          value={existingNote?.content || ''}
          readOnly
          disableToolbar
          borderless
        />
      ) : (
        <EditorRHF
          name={noteName}
          className={editorClassName}
          control={control}
          label={label}
          placeholder={placeholder}
          enableMentions={enableMentions}
          disabled={disabled}
          additionalToolbarButtons={[
            <button
              key="attachments"
              type="button"
              disabled={disabled}
              onClick={(e) => {
                if (disabled) return
                // Drop focus so it doesn't stay highlighted after the file dialog.
                e.currentTarget.blur()
                openFileSelect()
              }}
              // ``!`` on display/size/padding so this custom button beats
              // Quill snow's base ``.ql-toolbar button`` rule (28×24 +
              // padding + inline-block), which otherwise wins on specificity
              // and renders the paperclip as an off-center rectangle. The box
              // is sized in px (not ``w-7``) to match the format buttons,
              // which Editor.css pins to 28px — at this app's 14px root,
              // ``w-7`` (1.75rem) would only be 24.5px. The glyph uses the same
              // 1.05rem box as the Phosphor format icons so the whole toolbar
              // is one family at a consistent size.
              className="!flex items-center justify-center !h-[28px] !w-[28px] !p-0 rounded-md text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:!h-[1.05rem] [&_svg]:!w-[1.05rem]"
            >
              <PiPaperclipHorizontalBold />
            </button>,
          ]}
          toolbarSuffix={composedToolbarSuffix}
          toolbarClassName={props.toolbarClassName}
        />
      )}

      {/* View-mode attachment list (full display, not inline) */}
      {viewMode && allAttachments.length > 0 && (
        <div className="mt-3">
          <AttachmentList
            attachments={allAttachments}
            disabled={disabled}
            maxSize={MAX_NOTE_ATTACHMENT_SIZE}
            showSummary={shouldShowSummary}
          />
        </div>
      )}

      {!viewMode && (
        <input
          type="file"
          hidden
          multiple
          ref={inputRef}
          onChange={(e) => {
            // Convert existing File[] to Attachment[] for validation
            const currentAttachments = attachmentsValue.map((file) => ({
              file,
              filename: file.name,
              size: file.size,
            }))
            handleFileSelect(e, currentAttachments)
          }}
          accept={getAcceptString(ALLOWED_NOTE_ATTACHMENT_TYPES)}
          disabled={disabled}
        />
      )}
    </div>
  )
}
